import type { MouseEvent } from "react";

import { SortTypeEnum } from "@repo/helpers";
import { Button, ButtonTypes, Column, GridPreLoaded } from "@repo/ui";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useMemo, useState } from "react";

import type { ApplicationSetting } from "../../../models";
import type { Segment, SettingGridRow } from "../settingsTree";

import { rowAction } from "../settingForm";
import { settingTypeLabel } from "../settingsTree";
import { OrphanedBadge } from "./orphanedBadge";
import { SegmentRowsGrid } from "./segmentRowsGrid";
import { SettingFlagBadges } from "./settingFlagBadges";
import { SettingRowActions } from "./settingRowActions";
import { SourcePills } from "./sourcePills";
import style from "./style.module.scss";
import { ValueText } from "./valueText";

interface Props {
	rows: SettingGridRow[];
	loading?: boolean;
	/** Changes whenever the filters change, so the grid goes back to page 1 */
	filterKey: string;
	/** The segment the values are for, to mark which row applies in the sub-grid */
	segment?: Segment;
	/** Orphans have no definition, so the Default and Description columns are hidden */
	orphanedOnly?: boolean;
	/** Opens the add setting dialog */
	onAdd?: () => void;
	/** Opens the add setting dialog for a setting whose value doesn't come from a row of its own */
	onAddOverride?: (row: SettingGridRow) => void;
	/** Opens the dialog to edit a setting row */
	onEdit?: (setting: ApplicationSetting) => void;
	/** Asks before deleting a setting row */
	onDelete?: (setting: ApplicationSetting) => void;
	/** Reloads the settings and definitions from the server; the grid's refresh button calls it */
	onRefresh?: () => void;
	/** Shows a setting's details; a click anywhere on its row except the row's buttons calls it */
	onOpen?: (row: SettingGridRow) => void;
}

/** Keeps a click on a row's own buttons from also opening the setting's details */
const stopRowClick = (event: MouseEvent) => event.stopPropagation();

// ValueText cuts long values off itself: the grid's own char limit would turn the element into text
const defaultColumn = new Column<SettingGridRow>("Default", "defaultValue")
	.withFullObject()
	.withFormat((row: SettingGridRow) => (
		<ValueText value={row.defaultValue} enumOptions={row.enumOptions} charLimit={60} />
	));
const descriptionColumn = new Column<SettingGridRow>("Description", "description").withCharLimit(150);

const columns = [
	new Column<SettingGridRow>("Setting", "name")
		.withSortBy(SortTypeEnum.ascending)
		.withMinWidth(350)
		.withFullObject()
		.withFormat((row: SettingGridRow) => (
			<div className={style.settingCell}>
				<span className={style.settingName}>{row.name}</span>
				{row.friendlyName && <span className={style.friendlyName}>{row.friendlyName}</span>}
				{row.isOrphaned && <OrphanedBadge />}
				<SettingFlagBadges row={row} />
			</div>
		)),
	new Column<SettingGridRow>("Value", "value")
		.withFullObject()
		.withFormat((row: SettingGridRow) => (
			<ValueText value={row.value} enumOptions={row.enumOptions} charLimit={100} masked={row.winner?.isMasked} />
		)),
	new Column<SettingGridRow>("Source", "source")
		.withFullObject()
		.withFormat((row: SettingGridRow) => <SourcePills row={row} />),
	defaultColumn,
	new Column<SettingGridRow>("Type", "dataType").withFormat(settingTypeLabel),
	new Column<SettingGridRow>("Rows", undefined, SortTypeEnum.none, undefined, false).withFormat(
		(row: SettingGridRow) => row.rows.length || "",
	),
	descriptionColumn,
];

/** Without Default and Description, which come from the definition, so they're always empty for orphans */
const orphanColumns = columns.filter((c) => c !== defaultColumn && c !== descriptionColumn);

/** A copy of the set with the key added, or removed if it was there */
function toggled(keys: Set<string>, key: string) {
	const next = new Set(keys);
	if (!next.delete(key)) next.add(key);
	return next;
}

export function SettingsGrid({
	rows,
	loading,
	filterKey,
	segment,
	orphanedOnly,
	onAdd,
	onAddOverride,
	onEdit,
	onDelete,
	onRefresh,
	onOpen,
}: Props) {
	const [expanded, setExpanded] = useState<Set<string>>(new Set());

	const gridColumns = useMemo(() => {
		const expander = new Column<SettingGridRow>("", undefined, SortTypeEnum.none, undefined, false)
			.withId("expander")
			.withFormat((row: SettingGridRow) => {
				if (!row.rows.length) return null;
				const isExpanded = expanded.has(row.key);
				return (
					<Button
						onClick={(event) => {
							stopRowClick(event);
							setExpanded((keys) => toggled(keys, row.key));
						}}
						types={[ButtonTypes.Text]}
						title={isExpanded ? "Hide the rows" : "Show the rows"}
					>
						{isExpanded ? (
							<ChevronDown className={style.chevronIcon} />
						) : (
							<ChevronRight className={style.chevronIcon} />
						)}
					</Button>
				);
			});
		const actions = new Column<SettingGridRow>("", undefined, SortTypeEnum.none, undefined, false)
			.withId("actions")
			.withFormat((row: SettingGridRow) => {
				const action = rowAction(row);
				if (action === "edit" && onEdit && onDelete)
					return <SettingRowActions setting={row.winner} onEdit={onEdit} onDelete={onDelete} />;
				if (action === "addOverride" && onAddOverride)
					return (
						<Button
							types={[ButtonTypes.Text]}
							title="Add a row for this setting and the selected segment"
							onClick={(event) => {
								stopRowClick(event);
								onAddOverride(row);
							}}
						>
							Add override
						</Button>
					);
				return null;
			});
		// The grid keeps its first column order, so a column left out here comes back in place when it returns
		return [expander, ...(orphanedOnly ? orphanColumns : columns), actions];
	}, [expanded, orphanedOnly, onAddOverride, onEdit, onDelete]);

	const rowsByKey = useMemo(() => new Map(rows.map((row) => [row.key, row])), [rows]);

	function expandedDisplay(key: string) {
		const row = expanded.has(key) && rowsByKey.get(key);
		return row ? <SegmentRowsGrid row={row} segment={segment} onEdit={onEdit} onDelete={onDelete} /> : null;
	}

	function openRow(key: string) {
		const row = rowsByKey.get(key);
		if (row) onOpen?.(row);
	}

	return (
		<GridPreLoaded<SettingGridRow>
			id="appSettingsGrid"
			keyProperty="key"
			columns={gridColumns}
			values={rows}
			loading={loading}
			pageSize={25}
			resetPageKey={filterKey}
			expandedDisplay={expandedDisplay}
			onClickRow={onOpen ? openRow : undefined}
			rowClassName={onOpen ? style.clickableRow : undefined}
			createButtonText="Add setting"
			onCreateClick={onAdd}
			// GridPreLoaded calls getData only for the refresh button; paging and sorting happen in memory
			getData={onRefresh}
		/>
	);
}
